import { apiClient } from "@/lib/apiClient";
import type {
  Department,
  DepartmentsListParams,
  DepartmentsListResponse,
  CreateDepartmentPayload,
  UpdateDepartmentPayload,
} from "../types/departments.types";

const ENDPOINT = "/api/admin/departments";

/** Supports array `data`, paginated `{ items, total }`, or top-level `items`. */
function normalizeDepartmentsListBody(body: {
  data?:
    | Department[]
    | { items?: Department[]; total?: number; count?: number };
  items?: Department[];
  total?: number;
  count?: number;
}): { list: Department[]; total: number } {
  let list: Department[] = [];
  if (Array.isArray(body?.data)) {
    list = body.data;
  } else if (
    body?.data &&
    typeof body.data === "object" &&
    "items" in body.data
  ) {
    const items = (body.data as { items?: Department[] }).items;
    list = Array.isArray(items) ? items : [];
  } else if (Array.isArray(body?.items)) {
    list = body.items;
  }

  let total = body?.total ?? body?.count ?? list.length;
  if (
    body?.data &&
    typeof body.data === "object" &&
    !Array.isArray(body.data)
  ) {
    const nested = body.data as { total?: number; count?: number };
    if (typeof nested.total === "number") total = nested.total;
    else if (typeof nested.count === "number") total = nested.count;
  }

  return { list, total: Number(total) };
}

export const departmentsApi = {
  /**
   * GET /api/admin/departments - Paginated list.
   * Query: page, limit, search, type (1=CENTRAL, 2=BRANCH)
   */
  async getList(
    params: DepartmentsListParams = {},
  ): Promise<DepartmentsListResponse> {
    const { page = 1, limit = 20, search, type } = params;
    const query: Record<string, unknown> = { page, limit };
    if (search?.trim()) query.search = search.trim();
    if (type != null) query.type = type;

    const { data } = await apiClient.get<{
      data?:
        | Department[]
        | { items?: Department[]; total?: number; count?: number };
      items?: Department[];
      total?: number;
      count?: number;
    }>(ENDPOINT, { params: query });

    const { list, total } = normalizeDepartmentsListBody(data);
    return { data: list, total };
  },

  /**
   * GET /api/admin/departments/tree - Department hierarchy tree.
   * Optional `search` filters nodes server-side (case-insensitive) and keeps ancestors.
   */
  async getTree(search?: string): Promise<Department[]> {
    const params: Record<string, string> = {};
    if (search?.trim()) params.search = search.trim();
    const { data } = await apiClient.get<Department[] | { data?: Department[] }>(
      `${ENDPOINT}/tree`,
      { params },
    );
    if (Array.isArray(data)) return data;
    return (data as { data?: Department[] })?.data ?? [];
  },

  /**
   * GET /api/admin/departments/:id - Get department by id
   */
  async getById(id: string): Promise<Department> {
    const { data } = await apiClient.get<Department | { data?: Department }>(
      `${ENDPOINT}/${id}`,
    );
    const dept = (data as { data?: Department })?.data ?? data;
    if (!dept) throw new Error("Department not found");
    return dept as Department;
  },

  /**
   * POST /api/admin/departments - Create department
   */
  async create(payload: CreateDepartmentPayload): Promise<Department> {
    const body: Record<string, unknown> = {
      type: payload.type,
      name: payload.name.trim(),
    };
    if (payload.domainId) body.domainId = payload.domainId;
    if (payload.description != null) body.description = payload.description;
    if (payload.location != null) body.location = payload.location;
    if (payload.parentID != null && payload.parentID !== "")
      body.parentID = payload.parentID;
    if (payload.supervisorID != null && payload.supervisorID !== "")
      body.supervisorID = payload.supervisorID;

    const { data } = await apiClient.post<Department | { data?: Department }>(
      ENDPOINT,
      body,
      // Çağıran taraf placeholder düzeltmesi + döngüsel referans mesajını
      // özelleştiriyor; interceptor'ın global toast'ını burada bastırıyoruz.
      { skipErrorToast: true },
    );
    const dept = (data as { data?: Department })?.data ?? data;
    if (!dept) throw new Error("Create failed");
    return dept as Department;
  },

  /**
   * PATCH /api/admin/departments/:id - Update department
   */
  async update(
    id: string,
    payload: UpdateDepartmentPayload,
  ): Promise<Department> {
    const body: Record<string, unknown> = {};
    if (payload.type != null) body.type = payload.type;
    if (payload.domainId != null) body.domainId = payload.domainId;
    if (payload.name != null) body.name = payload.name.trim();
    if (payload.description !== undefined) body.description = payload.description;
    if (payload.location !== undefined) body.location = payload.location;
    if (payload.parentID !== undefined)
      body.parentID = payload.parentID === "" ? null : payload.parentID;
    if (payload.supervisorID !== undefined)
      body.supervisorID =
        payload.supervisorID === "" ? null : payload.supervisorID;

    const { data } = await apiClient.patch<Department | { data?: Department }>(
      `${ENDPOINT}/${id}`,
      body,
      // create() ile aynı sebep — özel mesaj catch'te düzeltiliyor.
      { skipErrorToast: true },
    );
    const dept = (data as { data?: Department })?.data ?? data;
    if (!dept) throw new Error("Update failed");
    return dept as Department;
  },

  /**
   * DELETE /api/admin/departments/:id - Delete department
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${ENDPOINT}/${id}`);
  },
};
